<template>
  <LoadingCard :loading="loading" class="overflow-visible">
    <!-- ─── List view ─────────────────────────────────────────── -->
    <template v-if="!selected">
    <!-- Header -->
    <div class="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b border-gray-100 dark:border-gray-700">
      <div class="flex items-center gap-3 min-w-0">
        <div class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400">
          <svg class="h-4.5 w-4.5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M11.584 2.376a.75.75 0 01.832 0l9 6a.75.75 0 11-.832 1.248L12 3.901 3.416 9.624a.75.75 0 01-.832-1.248l9-6z" />
            <path d="M20.25 10.332v9.918H21a.75.75 0 010 1.5H3a.75.75 0 010-1.5h.75v-9.918a.75.75 0 01.634-.74A49.109 49.109 0 0112 9c2.59 0 5.134.202 7.616.592a.75.75 0 01.634.74zm-7.5 2.418a.75.75 0 00-1.5 0v6.75a.75.75 0 001.5 0v-6.75zm3-.75a.75.75 0 01.75.75v6.75a.75.75 0 01-1.5 0v-6.75a.75.75 0 01.75-.75zM9 12.75a.75.75 0 00-1.5 0v6.75a.75.75 0 001.5 0v-6.75z" />
          </svg>
        </div>
        <div class="min-w-0">
          <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">آموزشگاه‌های زیرمجموعه</h3>
          <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">
            آموزشگاه‌هایی که کد کانون شما را ثبت کرده‌اند
          </p>
        </div>
      </div>

      <div class="flex items-center gap-2 shrink-0">
        <!-- Kanoon code, copyable -->
        <button
          v-if="kanoon.code"
          type="button"
          @click="copyCode"
          class="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-primary-300 dark:border-primary-700 bg-primary-50/60 dark:bg-primary-900/20 px-2.5 h-8 text-xs font-semibold text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-colors"
          :title="copied ? 'کپی شد' : 'کپی کد کانون'"
        >
          <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          <span dir="ltr">{{ kanoon.code }}</span>
          <span v-if="copied" class="text-emerald-600 dark:text-emerald-400">✓</span>
        </button>

        <span
          v-if="kanoon.type_label"
          class="inline-flex h-8 items-center rounded-lg bg-gray-100 dark:bg-gray-700/60 px-2.5 text-xs font-medium text-gray-600 dark:text-gray-300"
        >
          {{ kanoon.type_label }}
        </span>

        <button
          @click="fetch()"
          :disabled="loading"
          class="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:text-primary-600 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
          title="بارگذاری مجدد"
        >
          <svg :class="['h-4 w-4', loading ? 'animate-spin' : '']" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Search -->
    <div class="px-4 sm:px-6 py-3 border-b border-gray-100 dark:border-gray-700">
      <div class="relative max-w-sm">
        <input
          v-model="search"
          @input="onSearch"
          type="text"
          placeholder="جستجو بر اساس نام، کد یا شماره تماس..."
          class="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 pr-9 text-sm text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
        />
        <svg class="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
        </svg>
      </div>
    </div>

    <!-- Empty -->
    <div v-if="!loading && institutes.length === 0" class="flex flex-col items-center justify-center py-12 px-6 text-center">
      <p class="text-sm font-medium text-gray-600 dark:text-gray-300">
        {{ search ? 'آموزشگاهی با این مشخصات پیدا نشد' : 'هنوز هیچ آموزشگاهی زیرمجموعه شما نیست' }}
      </p>
      <p v-if="!search" class="text-xs text-gray-400 mt-1">
        کد کانون خود را با آموزشگاه‌ها به اشتراک بگذارید تا در پنل خود ثبتش کنند.
      </p>
    </div>

    <!-- Table -->
    <div v-else class="overflow-x-auto">
      <table class="w-full text-sm">
        <thead>
          <tr class="text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
            <th class="px-4 sm:px-6 py-2.5 text-start font-medium">آموزشگاه</th>
            <th class="px-4 py-2.5 text-start font-medium">کد</th>
            <th class="px-4 py-2.5 text-start font-medium">مدیر</th>
            <th class="px-4 py-2.5 text-start font-medium">تماس</th>
            <th class="px-4 py-2.5 text-center font-medium">دوره‌ها</th>
            <th class="px-4 py-2.5 text-start font-medium">وضعیت</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100 dark:divide-gray-700/60">
          <tr
            v-for="institute in institutes"
            :key="institute.id"
            @click="openInstitute(institute)"
            class="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
            title="مشاهده اطلاعات و دوره‌های آموزشگاه"
          >
            <td class="px-4 sm:px-6 py-3">
              <div class="flex items-center gap-2.5 min-w-0">
                <img
                  v-if="institute.logo_url"
                  :src="institute.logo_url"
                  class="h-8 w-8 shrink-0 rounded-full object-cover"
                  alt=""
                />
                <div
                  v-else
                  class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-xs font-semibold text-gray-500"
                >
                  {{ initial(institute.name) }}
                </div>
                <span class="truncate font-semibold text-gray-900 dark:text-gray-100">{{ institute.name }}</span>
              </div>
            </td>
            <td class="px-4 py-3 text-xs text-gray-500">
              <span dir="ltr">{{ institute.code }}</span>
            </td>
            <td class="px-4 py-3 text-gray-600 dark:text-gray-300">{{ institute.owner_name || '—' }}</td>
            <td class="px-4 py-3 text-xs text-gray-500">
              <span dir="ltr">{{ institute.phone || '—' }}</span>
            </td>
            <td class="px-4 py-3 text-center">
              <span class="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700/60 px-2 text-xs font-semibold text-gray-600 dark:text-gray-300">
                {{ toFa(institute.courses_count) }}
              </span>
            </td>
            <td class="px-4 py-3">
              <span :class="['inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium', statusClass(institute.status)]">
                {{ institute.status_label }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div
      v-if="meta.last_page > 1 || meta.total > 0"
      class="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-3 border-t border-gray-100 dark:border-gray-700"
    >
      <p class="text-xs text-gray-500 dark:text-gray-400">
        نمایش {{ toFa(meta.from) }} تا {{ toFa(meta.to) }} از {{ toFa(meta.total) }} آموزشگاه
      </p>

      <div v-if="meta.last_page > 1" class="flex items-center gap-1">
        <button
          @click="goTo(meta.current_page - 1)"
          :disabled="meta.current_page <= 1 || loading"
          class="inline-flex h-8 items-center rounded-lg border border-gray-200 dark:border-gray-600 px-3 text-xs text-gray-600 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700/50"
        >
          قبلی
        </button>

        <button
          v-for="page in pageWindow"
          :key="page"
          @click="goTo(page)"
          :disabled="loading"
          :class="[
            'inline-flex h-8 min-w-[2rem] items-center justify-center rounded-lg px-2 text-xs font-medium transition-colors',
            page === meta.current_page
              ? 'bg-primary-500 text-white'
              : 'border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50',
          ]"
        >
          {{ toFa(page) }}
        </button>

        <button
          @click="goTo(meta.current_page + 1)"
          :disabled="meta.current_page >= meta.last_page || loading"
          class="inline-flex h-8 items-center rounded-lg border border-gray-200 dark:border-gray-600 px-3 text-xs text-gray-600 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700/50"
        >
          بعدی
        </button>
      </div>
    </div>

    </template>

    <!-- ─── Detail view ───────────────────────────────────────── -->
    <template v-else>
      <div>
        <!-- Detail header -->
        <div class="flex items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <div class="flex items-center gap-3 min-w-0">
            <button
              @click="closeInstitute"
              class="shrink-0 h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
              title="بازگشت به فهرست آموزشگاه‌ها"
            >
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <img
              v-if="profile.logo_url ?? selected.logo_url"
              :src="profile.logo_url ?? selected.logo_url"
              class="h-10 w-10 shrink-0 rounded-full object-cover"
              alt=""
            />
            <div
              v-else
              class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-sm font-semibold text-gray-500"
            >
              {{ initial(profile.name ?? selected.name) }}
            </div>
            <div class="min-w-0">
              <h2 class="text-sm sm:text-base font-bold text-gray-900 dark:text-gray-100 truncate">
                {{ profile.name ?? selected.name }}
              </h2>
              <p class="text-xs text-gray-400 mt-0.5">
                <span dir="ltr">{{ profile.code ?? selected.code }}</span>
                <span v-if="profile.status_label"> · {{ profile.status_label }}</span>
              </p>
            </div>
          </div>

          <button
            @click="closeInstitute"
            class="shrink-0 inline-flex h-8 items-center rounded-lg border border-gray-200 dark:border-gray-600 px-3 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50"
          >
            بازگشت به فهرست
          </button>
        </div>

        <div class="p-4 sm:p-6 space-y-5">
          <div v-if="detailLoading" class="flex justify-center py-16 text-gray-400">
            <svg class="h-6 w-6 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>

          <template v-else>
            <!-- Stats -->
            <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              <div v-for="tile in statTiles" :key="tile.label" class="rounded-xl border border-gray-100 dark:border-gray-700 px-3 py-2.5">
                <p class="text-[11px] text-gray-400">{{ tile.label }}</p>
                <p class="mt-1 text-sm font-bold text-gray-900 dark:text-gray-100">{{ toFa(tile.value) }}</p>
              </div>
            </div>

            <!-- Profile -->
            <div class="rounded-xl border border-gray-100 dark:border-gray-700 px-4 py-3">
              <p class="mb-2 text-xs font-semibold text-gray-500 dark:text-gray-400">اطلاعات آموزشگاه</p>
              <dl class="grid gap-x-6 gap-y-2 sm:grid-cols-2">
                <div v-for="row in profileRows" :key="row.label" class="text-xs">
                  <dt class="text-gray-400 mb-0.5">{{ row.label }}</dt>
                  <dd class="text-gray-800 dark:text-gray-200 break-words" :dir="row.ltr ? 'ltr' : null">
                    {{ row.value || '—' }}
                  </dd>
                </div>
              </dl>
            </div>

            <!-- Courses -->
            <div>
              <div class="mb-2 flex items-center justify-between gap-2">
                <p class="text-xs font-semibold text-gray-500 dark:text-gray-400">دوره‌های آموزشگاه</p>
                <p v-if="coursesMeta.total" class="text-xs text-gray-400">
                  {{ toFa(coursesMeta.total) }} دوره
                </p>
              </div>

              <p v-if="courses.length === 0" class="rounded-xl border border-dashed border-gray-200 dark:border-gray-700 py-8 text-center text-sm text-gray-500">
                این آموزشگاه هنوز دوره‌ای ثبت نکرده است.
              </p>

              <div v-else class="overflow-x-auto rounded-xl border border-gray-100 dark:border-gray-700">
                <table class="w-full text-sm">
                  <thead>
                    <tr class="text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                      <th class="px-4 py-2.5 text-start font-medium">عنوان</th>
                      <th class="px-4 py-2.5 text-start font-medium">دسته‌بندی</th>
                      <th class="px-4 py-2.5 text-start font-medium">نوع</th>
                      <th class="px-4 py-2.5 text-start font-medium">سطح</th>
                      <th class="px-4 py-2.5 text-center font-medium">ثبت‌نام</th>
                      <th class="px-4 py-2.5 text-start font-medium">قیمت</th>
                      <th class="px-4 py-2.5 text-start font-medium">وضعیت</th>
                      <th class="px-4 py-2.5 text-start font-medium"></th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-100 dark:divide-gray-700/60">
                    <tr v-for="course in courses" :key="course.id">
                      <td class="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100">{{ course.title }}</td>
                      <td class="px-4 py-2.5 text-xs text-gray-500">{{ course.category || '—' }}</td>
                      <td class="px-4 py-2.5 text-xs text-gray-500">{{ course.type_label }}</td>
                      <td class="px-4 py-2.5 text-xs text-gray-500">{{ course.level_label }}</td>
                      <td class="px-4 py-2.5 text-center text-gray-600 dark:text-gray-300">{{ toFa(course.enrollments_count) }}</td>
                      <td class="px-4 py-2.5 text-gray-700 dark:text-gray-300">{{ money(course.final_price) }}</td>
                      <td class="px-4 py-2.5">
                        <span :class="['inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium', courseStatusClass(course.status)]">
                          {{ course.status_label }}
                        </span>
                      </td>
                      <td class="px-4 py-2.5">
                        <button
                          type="button"
                          @click="openCourse(course)"
                          class="inline-flex h-7 items-center rounded-lg border border-gray-200 dark:border-gray-600 px-2.5 text-[11px] text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                        >
                          جزئیات
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Courses pagination -->
              <div v-if="coursesMeta.last_page > 1" class="mt-3 flex items-center justify-end gap-1">
                <button
                  @click="goToCoursesPage(coursesMeta.current_page - 1)"
                  :disabled="coursesMeta.current_page <= 1 || detailLoading"
                  class="inline-flex h-8 items-center rounded-lg border border-gray-200 dark:border-gray-600 px-3 text-xs text-gray-600 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  قبلی
                </button>
                <span class="px-2 text-xs text-gray-500">
                  {{ toFa(coursesMeta.current_page) }} / {{ toFa(coursesMeta.last_page) }}
                </span>
                <button
                  @click="goToCoursesPage(coursesMeta.current_page + 1)"
                  :disabled="coursesMeta.current_page >= coursesMeta.last_page || detailLoading"
                  class="inline-flex h-8 items-center rounded-lg border border-gray-200 dark:border-gray-600 px-3 text-xs text-gray-600 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  بعدی
                </button>
              </div>
            </div>
          </template>
        </div>
      </div>
    </template>

    <!-- Course detail modal -->
    <Modal v-if="selectedCourse" :show="true" size="3xl" @close-via-escape="closeCourse">
      <div class="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-lg max-h-[90vh] flex flex-col">
        <div class="flex items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b border-gray-100 dark:border-gray-700 shrink-0">
          <div class="min-w-0">
            <h2 class="text-sm sm:text-base font-bold text-gray-900 dark:text-gray-100 truncate">
              {{ selectedCourse.title }}
            </h2>
            <p class="text-xs text-gray-400 truncate mt-0.5">
              {{ profile.name ?? selected?.name }}
            </p>
          </div>
          <button
            @click="closeCourse"
            class="shrink-0 h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="flex-1 overflow-y-auto p-4 sm:p-6">
          <KanoonCourseContent :course-id="selectedCourse.id" />
        </div>
      </div>
    </Modal>
  </LoadingCard>
</template>

<script>
import jMoment from 'moment-jalaali'

export default {
  name: 'KanoonInstituteListCard',

  props: {
    card: { type: Object, required: true },
  },

  data: () => ({
    loading: true,
    institutes: [],
    kanoon: {},
    meta: { current_page: 1, last_page: 1, total: 0, from: 0, to: 0 },
    search: '',
    searchTimer: null,
    copied: false,
    selected: null,
    detail: null,
    detailLoading: false,
    selectedCourse: null,
  }),

  computed: {
    endpoint() {
      return this.card?.apiEndpoint ?? '/dashboard-api/kanoon/institutes'
    },

    profile() {
      return this.detail?.institute ?? {}
    },

    courses() {
      return this.detail?.courses ?? []
    },

    coursesMeta() {
      return this.detail?.courses_meta ?? { current_page: 1, last_page: 1, total: 0 }
    },

    statTiles() {
      const stats = this.detail?.stats ?? {}

      return [
        { label: 'کل دوره‌ها', value: stats.courses_total },
        { label: 'منتشرشده', value: stats.courses_published },
        { label: 'در انتظار تأیید', value: stats.courses_pending },
        { label: 'پیش‌نویس', value: stats.courses_draft },
        { label: 'مهارت‌آموزان', value: stats.students_total },
        { label: 'شعبه‌ها', value: this.profile.branches_count },
      ]
    },

    profileRows() {
      const p = this.profile

      return [
        { label: 'مدیر', value: p.owner_name },
        { label: 'شماره همراه', value: p.phone, ltr: true },
        { label: 'تلفن مرکز', value: p.center_phone_number, ltr: true },
        { label: 'پست الکترونیک', value: p.email, ltr: true },
        { label: 'شماره پروانه', value: p.license_number, ltr: true },
        { label: 'ساعات کاری', value: p.opening_hours },
        { label: 'تعداد مدرسان', value: p.instructors_count != null ? this.toFa(p.instructors_count) : null },
        { label: 'تاریخ عضویت', value: this.jalali(p.joined_at), ltr: true },
        { label: 'آدرس', value: p.address },
        { label: 'توضیحات', value: p.description },
      ]
    },

    // Keep the pager to a sliding window so a kanoon with many pages stays readable
    pageWindow() {
      const last = this.meta.last_page
      const current = this.meta.current_page
      const start = Math.max(1, Math.min(current - 2, last - 4))
      const end = Math.min(last, start + 4)
      const pages = []
      for (let page = start; page <= end; page++) pages.push(page)
      return pages
    },
  },

  created() {
    this.fetch()
    this.restoreFromUrl()
  },

  beforeUnmount() {
    clearTimeout(this.searchTimer)
  },

  methods: {
    initial(name) {
      return (name ?? '').trim().charAt(0) || '?'
    },

    toFa(value) {
      if (value == null) return '۰'
      return Number(value).toLocaleString('fa-IR')
    },

    statusClass(status) {
      const map = {
        active: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
        pending: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
        suspended: 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400',
      }
      return map[status] ?? 'bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300'
    },

    onSearch() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.fetch(1), 400)
    },

    goTo(page) {
      if (page < 1 || page > this.meta.last_page) return
      this.fetch(page)
    },

    async fetch(page = null) {
      this.loading = true
      try {
        const { data } = await Nova.request().get(this.endpoint, {
          params: {
            page: page ?? this.meta.current_page,
            search: this.search || undefined,
          },
        })
        this.institutes = data.institutes ?? []
        this.kanoon = data.kanoon ?? {}
        this.meta = data.meta ?? this.meta
      } catch (error) {
        this.institutes = []
        Nova.error(error?.response?.data?.message ?? 'دریافت لیست آموزشگاه‌ها با خطا مواجه شد.')
      } finally {
        this.loading = false
      }
    },

    money(value) {
      return `${this.toFa(value ?? 0)} تومان`
    },

    jalali(value) {
      if (!value) return null
      return jMoment(value, 'YYYY-MM-DD HH:mm:ss').format('jYYYY/jMM/jDD')
    },

    courseStatusClass(status) {
      const map = {
        published: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
        pending: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
        draft: 'bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300',
        archived: 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400',
      }
      return map[status] ?? 'bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300'
    },

    openInstitute(institute) {
      this.selected = institute
      this.detail = null
      this.syncUrl(institute.id)
      this.fetchDetail()
    },

    closeInstitute() {
      this.selected = null
      this.detail = null
      this.selectedCourse = null
      this.syncUrl(null)
    },

    openCourse(course) {
      this.selectedCourse = course
    },

    closeCourse() {
      this.selectedCourse = null
    },

    // Keeping the open institute in the query string makes the detail view
    // survive a page refresh and stay shareable, without a Nova page route.
    syncUrl(instituteId) {
      const url = new URL(window.location.href)

      if (instituteId) {
        url.searchParams.set('institute', instituteId)
      } else {
        url.searchParams.delete('institute')
      }

      window.history.replaceState({}, '', url)
    },

    restoreFromUrl() {
      const instituteId = Number(new URLSearchParams(window.location.search).get('institute'))

      if (!instituteId) return

      this.selected = { id: instituteId }
      this.fetchDetail()
    },

    goToCoursesPage(page) {
      if (page < 1 || page > this.coursesMeta.last_page) return
      this.fetchDetail(page)
    },

    async fetchDetail(coursesPage = null) {
      const instituteId = this.selected?.id

      if (!instituteId) return

      this.detailLoading = true
      try {
        const { data } = await Nova.request().get(`${this.endpoint}/${instituteId}`, {
          params: { courses_page: coursesPage ?? undefined },
        })
        this.detail = data
      } catch (error) {
        this.closeInstitute()
        Nova.error(error?.response?.data?.message ?? 'دریافت اطلاعات آموزشگاه با خطا مواجه شد.')
      } finally {
        this.detailLoading = false
      }
    },

    async copyCode() {
      try {
        await navigator.clipboard.writeText(this.kanoon.code)
        this.copied = true
        setTimeout(() => (this.copied = false), 2000)
      } catch {
        Nova.error('کپی کد کانون ممکن نشد.')
      }
    },
  },
}
</script>
